import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import {
  type AccountRequest,
  type AccountRequestDecision,
  useAdminAccountRequests,
  useApproveAccountRequest,
  useApproveOrgAccountRequest,
  useDenyAccountRequest,
  useDenyOrgAccountRequest,
  useOrgAccountRequests,
} from '@/lib/accountRequests';
import { useAdminOrgs } from '@/lib/admin';

/**
 * Shared queue UI for account requests. Two modes via the `scope` prop:
 *
 * - `{ kind: 'site' }` — site-admin queue at /admin. Sees every
 *   request regardless of which org it targets, with an "attach to org"
 *   override on approval.
 * - `{ kind: 'org', orgSlug }` — org-admin queue under
 *   /orgs/{slug}. Sees only requests targeting this org; approval
 *   always pins to this org (no override needed).
 *
 * Empty queue → render nothing for org scope (so org admins don't see
 * a useless empty section), render an empty-state in site scope.
 */
type Scope =
  | { kind: 'site' }
  | { kind: 'org'; orgSlug: string };

export function AccountRequestsPanel({ scope }: { scope: Scope }) {
  const isSite = scope.kind === 'site';
  const siteQueue = useAdminAccountRequests('pending');
  const orgQueue = useOrgAccountRequests(
    isSite ? undefined : scope.orgSlug,
    'pending',
  );
  const queue = isSite ? siteQueue : orgQueue;

  if (!isSite) {
    if (queue.isPending) return null;
    if (!queue.data || queue.data.length === 0) return null;
  }

  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold">Account requests</h2>
        {queue.data && queue.data.length > 0 && (
          <span className="rounded-full bg-forge-100 dark:bg-forge-900 px-2 py-0.5 text-xs font-medium text-forge-700 dark:text-forge-300">
            {queue.data.length} pending
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        {isSite
          ? "Requests with no org preference go here. Approving creates the user with a personal org and emails them a 'set your password' link."
          : "Requests targeting this org. Approving creates the user, attaches them as a member of this org (no personal org), and emails them a 'set your password' link."}
      </p>

      <div className="mt-4 space-y-3">
        {queue.isPending && (
          <p className="text-sm text-stone-500 dark:text-stone-400">Loading…</p>
        )}
        {queue.data && queue.data.length === 0 && (
          <p className="text-sm text-stone-500 dark:text-stone-400">
            No pending requests.
          </p>
        )}
        {queue.data?.map((req) => (
          <RequestRow key={req.id} request={req} scope={scope} />
        ))}
      </div>
    </section>
  );
}

function RequestRow({
  request,
  scope,
}: {
  request: AccountRequest;
  scope: Scope;
}) {
  const isSite = scope.kind === 'site';
  // `useAdminOrgs` is a site-admin-only endpoint; only call it when
  // we actually need to resolve an org id to a display name (i.e.
  // in the site-admin scope where the request can target any org).
  // In org-scope, every visible request targets the current org by
  // definition, so the lookup is redundant.
  const orgs = useAdminOrgs(isSite ? undefined : { enabled: false });

  // Hooks for each scope. Always call both; only one path is used.
  const siteApprove = useApproveAccountRequest();
  const siteDeny = useDenyAccountRequest();
  const orgApprove = useApproveOrgAccountRequest(
    isSite ? undefined : scope.orgSlug,
  );
  const orgDeny = useDenyOrgAccountRequest(
    isSite ? undefined : scope.orgSlug,
  );

  const approve = isSite ? siteApprove : orgApprove;
  const deny = isSite ? siteDeny : orgDeny;

  const [note, setNote] = useState('');
  const [orgId, setOrgId] = useState<number | ''>(
    request.requested_org_id ?? '',
  );

  const requestedOrg =
    isSite && request.requested_org_id
      ? orgs.data?.find((o) => o.id === request.requested_org_id)
      : null;
  const created = formatRelative(request.created_at);

  const onApprove = () => {
    const body: AccountRequestDecision = {
      note: note.trim() || null,
      // Site scope sends the override; org scope ignores it (always
      // pins to the panel's org server-side).
      ...(isSite ? { organization_id: orgId === '' ? null : orgId } : {}),
    };
    approve.mutate({ id: request.id, body });
  };

  const onDeny = () =>
    deny.mutate({
      id: request.id,
      body: { note: note.trim() || null },
    });

  const busy = approve.isPending || deny.isPending;
  const error =
    approve.isError ? (approve.error as Error) :
    deny.isError ? (deny.error as Error) : null;

  return (
    <div className="rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950/40 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="font-medium">{request.email}</span>
          {request.display_name && (
            <span className="ml-2 text-sm text-stone-600 dark:text-stone-400">
              ({request.display_name})
            </span>
          )}
        </div>
        <span className="text-xs text-stone-500 dark:text-stone-400">
          {created}
        </span>
      </div>

      {(requestedOrg || request.requested_org_name) && (
        <p className="mt-1 text-xs text-stone-600 dark:text-stone-400">
          Requested org:{' '}
          <span className="font-medium">
            {requestedOrg ? requestedOrg.name : request.requested_org_name}
          </span>
        </p>
      )}
      {request.reason && (
        <p className="mt-2 whitespace-pre-wrap text-sm text-stone-700 dark:text-stone-300">
          {request.reason}
        </p>
      )}

      <div className={`mt-3 grid gap-3 ${isSite ? 'sm:grid-cols-2' : ''}`}>
        {isSite && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Attach to org (on approve)
            </span>
            <select
              value={orgId}
              onChange={(e) =>
                setOrgId(e.target.value === '' ? '' : Number(e.target.value))
              }
              className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1 text-sm"
            >
              <option value="">— none (auto-create starter org) —</option>
              {orgs.data?.filter((o) => o.member_count >= 2).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
            Note to requester (optional)
          </span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Shown in the welcome / denial email"
            className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1 text-sm"
          />
        </label>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button onClick={onApprove} disabled={busy}>
          {approve.isPending ? 'Approving…' : 'Approve'}
        </Button>
        <Button variant="secondary" onClick={onDeny} disabled={busy}>
          {deny.isPending ? 'Denying…' : 'Deny'}
        </Button>
        {error && (
          <span className="text-sm text-red-600">{error.message}</span>
        )}
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86_400)}d ago`;
}
